import { formatLocalDateTime } from '../../utils/localDateTime';
import React, { useEffect, useRef, useState } from 'react';
import { collection, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../../firebaseConfig';
import DeliveredReportAttachment from '../ConsultantReportViewer';

export interface SupportManager {
    uid: string;
    email: string;
    displayName: string;
    adminRole: string;
}

export interface SupportChatRecord {
    id: string;
    organizationId: string;
    organizationName: string;
    region: string;
    managerUid: string;
    managerEmail: string;
    managerName: string;
    participantUid: string;
    participantEmail: string;
    participantName: string;
    summary: string;
    status: string;
    startedAt: any;
    lastMessageAt: any;
    lastMessagePreview: string;
    lastMessageSenderType: 'client' | 'admin';
    unreadForManager?: boolean;
    unreadForClient?: boolean;
}

interface SupportMessage {
    id: string;
    senderUid?: string;
    senderEmail?: string;
    senderName: string;
    senderType: 'client' | 'admin';
    body: string;
    sentAt: any;
    reports?: Array<{ id: string; title: string }>;
}

interface Props {
    chat?: SupportChatRecord | null;
    managers?: SupportManager[];
    mode: 'client' | 'admin';
    onClose: () => void;
    onCreated?: (chatId: string) => void;
}

const timestamp = (value: any) => {
    return formatLocalDateTime(value, 'Sending…');
};

const isParticipantMessage = (message: SupportMessage, chat: SupportChatRecord) =>
    Boolean(message.senderUid && message.senderUid === chat.participantUid) ||
    Boolean(message.senderEmail && message.senderEmail.toLowerCase() === chat.participantEmail.toLowerCase());

const SupportChatModal: React.FC<Props> = ({ chat, managers = [], mode, onClose, onCreated }) => {
    const [messages, setMessages] = useState<SupportMessage[]>([]);
    const [message, setMessage] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const endRef = useRef<HTMLDivElement>(null);
    const call = async (action: string, data: Record<string, unknown>) => {
        const callable = httpsCallable(getFunctions(undefined, 'us-central1'), 'manageSupportChat');
        return callable({ action, data });
    };

    useEffect(() => {
        if (!chat)
            return;
        const messagesQuery = query(collection(db, 'support_chat_messages'), where('chatId', '==', chat.id), orderBy('sentAt', 'asc'));
        const unsubscribe = onSnapshot(messagesQuery, snapshot => {
            setMessages(snapshot.docs.map(document => ({ id: document.id, ...document.data() } as SupportMessage)));
        }, snapshotError => setError(snapshotError.message));
        void call('markRead', { chatId: chat.id, actorMode: mode });
        return unsubscribe;
    }, [chat?.id]);

    useEffect(() => {
        const messageEnd = endRef.current;
        if (typeof messageEnd?.scrollIntoView === 'function')
            messageEnd.scrollIntoView({ behavior: 'smooth' });
    }, [messages.length]);

    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        const body = message.trim();
        if (!body)
            return;
        setBusy(true);
        setError('');
        try {
            if (chat) {
                await call('sendMessage', { chatId: chat.id, message: body, actorMode: mode });
                setMessage('');
            } else {
                const response = await call('createChat', { message: body });
                const chatId = String((response.data as any)?.chatId || '');
                if (!chatId)
                    throw new Error('The chat could not be created.');
                onCreated?.(chatId);
            }
        } catch (submitError: any) {
            setError(submitError.message || 'The message could not be sent.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[220] flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-label={chat ? 'Consultant chat' : 'New consultant chat'} onMouseDown={event => event.target === event.currentTarget && onClose()}>
            <div className="flex h-[min(720px,90vh)] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
                <header className="flex items-start justify-between gap-4 border-b border-slate-200 bg-slate-900 px-5 py-4 text-white">
                    <div>
                        <h2 className="text-lg font-extrabold">{chat ? chat.organizationName : 'New Consultant Chat'}</h2>
                        <p className="mt-1 text-xs text-slate-300">
                            {chat
                                ? mode === 'admin' ? `${chat.participantName} · ${chat.participantEmail} · ${chat.region}` : `Assigned consultant: ${chat.managerName}`
                                : 'Messages are saved for your assigned consultant.'}
                        </p>
                    </div>
                    <button type="button" onClick={onClose} className="rounded-lg px-3 py-1 text-xl text-slate-300 hover:bg-white/10 hover:text-white" aria-label="Close chat">×</button>
                </header>

                {chat ? (
                    <div className="flex-1 space-y-3 overflow-y-auto bg-slate-50 p-5">
                        {messages.map(item => {
                            const fromParticipant = isParticipantMessage(item, chat);
                            const mine = mode === 'client' ? fromParticipant : !fromParticipant;
                            return (
                                <div key={item.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                                    <div className={`max-w-[82%] rounded-2xl px-4 py-3 shadow-sm ${mine ? 'bg-blue-600 text-white' : 'border border-slate-200 bg-white text-slate-800'}`}>
                                        <p className={`mb-1 text-[10px] font-bold uppercase tracking-wide ${mine ? 'text-blue-100' : 'text-slate-400'}`}>{item.senderName}</p>
                                        <p className="whitespace-pre-wrap text-sm leading-relaxed">{item.body}</p>
                                        {item.reports?.map(report => <DeliveredReportAttachment key={report.id} report={report} onOpened={onClose} />)}
                                        <p className={`mt-2 text-[10px] ${mine ? 'text-blue-100' : 'text-slate-400'}`}>{timestamp(item.sentAt)}</p>
                                    </div>
                                </div>
                            );
                        })}
                        {!messages.length && !error && <p className="py-10 text-center text-sm text-slate-400">Loading messages…</p>}
                        <div ref={endRef} />
                    </div>
                ) : (
                    <div className="flex-1 space-y-5 overflow-y-auto p-5">
                        {managers.length === 1 && <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-800">Your assigned consultant is <strong>{managers[0].displayName}</strong>.</div>}
                        {!managers.length && <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">No assigned consultant is currently available.</div>}
                    </div>
                )}

                <form onSubmit={submit} className="border-t border-slate-200 bg-white p-4">
                    {error && <p className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">{error}</p>}
                    <div className="flex items-end gap-3">
                        <textarea value={message} onChange={event => setMessage(event.target.value)} placeholder={chat ? 'Write a reply…' : 'How can your assigned consultant help?'} rows={3} maxLength={5000} className="min-h-[76px] flex-1 resize-none rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                        <button disabled={busy || !message.trim() || (!chat && !managers.length)} className="rounded-xl bg-blue-600 px-5 py-3 text-sm font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-40">{busy ? 'Sending…' : chat ? 'Send' : 'Start Chat'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SupportChatModal;
